import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import SelectInput from 'components/input/SelectInput';
import TextInput from 'components/input/textInput';
import YesNoSwitch from 'components/input/YesNoSwitch';
import TextAreaInput from 'components/input/TextAreaInput';
import GroupCheckbox from 'components/input/GroupCheckbox';
import { BoolToString, formatBoolean, optionFromArray } from 'components/helper/helper';
import DatePickerInput from 'components/input/DatePicker';
import CheckboxInput from 'components/input/CheckboxInput';
import {
  activityNumberOfMonthAfter12,
  activityNumberOfYearAfter12,
  sportOptions,
} from 'components/helper/appConfig';
import Grid from '@mui/joy/Grid';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Props {
  handleSave: (value: any) => void;
  item: any;
  indexNumber: String;
  expandAll?: boolean;
  handleExpandActive: (value: any) => void;
  check?: any;
  indexExpand?: any;
  isMobile?: boolean;
  disabled?: boolean;
}
const ACTIVITY_TYPE_RENAME_MAP: Record<string, string> = {
  'Athletics: Club': 'Athletics (other)',
  'Athletics: JV/Varsity': 'Athletics (JV or varsity)',
  'Career Oriented': 'Career readiness or practicums',
  'Community Service (Volunteer)': 'Community service or volunteer work',
  'Computer/Technology': 'Computer or technology',
  'Debate/Speech': 'Debate or speech',
  'Work (Paid)': 'Employment or work (paid)',
  'Journalism/Publication': 'Journalism or publishing',
  LGBT: 'LGBT+',
  'Music: Instrumental': 'Music',
  'Music: Vocal': 'Music',
  'Junior R.O.T.C.': 'R.O.T.C or military',
  'Science/Math': 'Science or math',
  'Social Justice': 'Social justice or organizing',
  'Student Govt./Politics': 'Student government or politics',
  'Foreign Exchange': 'Study abroad or foreign exchange',
  'Theater/Drama': 'Theater or drama',
  'Other Club/Activity': 'Other',
};

const mapActivityType = (activityType?: string) => {
  if (!activityType) return activityType;
  return ACTIVITY_TYPE_RENAME_MAP[activityType] ?? activityType;
};

const ActivitiesConfig = (props: Props) => {
  // const [countForm, setCountForm] = React.useState(0);
  const { check, isMobile = false, disabled = false } = props;

  // Educational Preparation Program — must exact-match UC edPrepId dropdown
  // Source: UC live screenshot 2026-08-20 (long labels, not spreadsheet abbreviations)
  const programForEduPreparOptions = [
    'ATDP (Academic Talent Development Program)',
    'boost@BerkeleyHaas',
    'California State Summer School for Mathematics and Science (COSMOS)',
    'Cal-SOAP',
    'CAMP (California Alliance for Minority Participation in Science)',
    'CBOP (Career Based Outreach Program)',
    'College Bound Academy Programs',
    'Council of African American Parents (CAAP)',
    'C-STEM',
    'Early Academic Outreach Program (EAOP)',
    'Educational Guidance Center (EGC)',
    'Educational Talent Search',
    'GEAR UP',
    'MESA (Mathematics, Engineering, Science Achievement) including MESA Schools Programs, SUCCESS Through Collaboration and California Community College Program',
    'PDP (Professional Development Program)',
    'Puente Program',
    'UC Links',
    'UC Merced Transfer Initiative Programs',
    'UC Scholars',
    'UCD Sacramento Area Youth Speaks (SAYS)',
    'UCLA Center for Community College Partnerships (CCCP)/SITE',
    'UCLA VIP Scholars Program',
    'UCSC SAGE (Students Acquiring "A-G" Expectations)',
    'UCSF Internships',
    'Upward Bound',
    'Wonderful Education',
    'Other',
  ];

  const EDU_PREP_RENAME_MAP: Record<string, string> = {
    // Spreadsheet / short abbreviations → UC live labels
    ATDP: 'ATDP (Academic Talent Development Program)',
    COSMOS: 'California State Summer School for Mathematics and Science (COSMOS)',
    'COSMOS\tCalifornia State Summer School for Mathematics and Science (COSMOS)':
      'California State Summer School for Mathematics and Science (COSMOS)',
    'COSMOS California State Summer School for Mathematics and Science (COSMOS)':
      'California State Summer School for Mathematics and Science (COSMOS)',
    CAMP: 'CAMP (California Alliance for Minority Participation in Science)',
    CBOP: 'CBOP (Career Based Outreach Program)',
    EAOP: 'Early Academic Outreach Program (EAOP)',
    MESA: 'MESA (Mathematics, Engineering, Science Achievement) including MESA Schools Programs, SUCCESS Through Collaboration and California Community College Program',
    PDP: 'PDP (Professional Development Program)',
    'UCD SAYS': 'UCD Sacramento Area Youth Speaks (SAYS)',
    'UCLA CCCP/SITE': 'UCLA Center for Community College Partnerships (CCCP)/SITE',
    'UCLA VIP Scholars': 'UCLA VIP Scholars Program',
    'UCSC SAGE': 'UCSC SAGE (Students Acquiring "A-G" Expectations)',
    'Young Entrepreneurs at Haas': 'boost@BerkeleyHaas',
    'Young Entrepreneurs at HAAS': 'boost@BerkeleyHaas',
  };

  const mapEduPrepProgram = (program?: string) => {
    if (!program) return program;
    return EDU_PREP_RENAME_MAP[program] ?? program;
  };


  const formItem = useFormik({
    initialValues: {
      category: 'Academic Activity',
      categoryUC: '',
      activityType: 'Academic',
      programName: '',
      activityNameUC: '',
      activityExperienceProgramName: '',
      isInvolvedLeadershipRole: '',
      positionDescription: '',
      isCurrentlyParticipatingActivity: false,
      timmingOfParticipation: '',
      timmingOfParticipation_duringYear: false,
      timmingOfParticipation_duringBreak: false,
      timmingOfParticipation_allYear: false,
      hoursPerWeek: 0,
      weeksPerYear: 0,
      isIntendParticipateSimilarActivity: false,
      listIndividualDistinctions: '',
      organizationVolunteeredFor: '',
      describeActivity: '',
      whatDidYouDo: '',
      programNameForEducationPreparation: '',
      programNameForEducationPreparationSpecify: '',
      descriptionExperience: '',
      describeOrganization: '',
      describeCompany: '',
      isStillWork: '',
      startDate: '',
      endDate: '',
      hoursPerWeekLowEnd: '',
      hoursPerWeekHighEnd: '',
      otherCoursewordName: '',
      isGrade9ParticipationLevels: false,
      isGrade10ParticipationLevels: false,
      isGrade11ParticipationLevels: false,
      isGrade12ParticipationLevels: false,
      isPostGraduateParticipationLevels: false,
      // usersnap 592
      sportOrTeam: '',
      // usersnap 540
      workExperience_whereDidYouWork: '',
      workExperience_jobTitle: '',
      workExperience_jobResponsibilities: '',
      workExperience_workWhenNinthGrade: false,
      workExperience_workWhenTenthGrade: false,
      workExperience_workWhenEleventhGrade: false,
      workExperience_workWhenTwelfthGrade: false,
      workExperience_ninthGradeSchoolYear: false,
      workExperience_ninthGradeSchoolYearHoursPerWeek: '',
      workExperience_ninthGradeSummer: false,
      workExperience_ninthGradeSummerHoursPerWeek: '',
      workExperience_tenthGradeSchoolYear: false,
      workExperience_tenthGradeSchoolYearHoursPerWeek: '',
      workExperience_tenthGradeSummer: false,
      workExperience_tenthGradeSummerHoursPerWeek: '',
      workExperience_eleventhGradeSchoolYear: false,
      workExperience_eleventhGradeSchoolYearHoursPerWeek: '',
      workExperience_eleventhGradeSummer: false,
      workExperience_eleventhGradeSummerHoursPerWeek: '',
      workExperience_twelfthGradeSchoolYear: false,
      workExperience_twelfthGradeSchoolYearHoursPerWeek: '',
      workExperience_twelfthGradeSummer: false,
      workExperience_twelfthGradeSummerHoursPerWeek: '',
      workExperience_workAfterTwelfthGrade: false,
      workExperience_afterTwelfthGradeSchoolYear: false,
      workExperience_afterTwelfthGradeSchoolYearHoursPerWeek: '',
      workExperience_afterTwelfthGradeSummer: false,
      workExperience_afterTwelfthGradeSummerHoursPerWeek: '',
      workExperience_afterTwelfthGradeSummerYear: '',
      workExperience_afterTwelfthGradeSummerMonth: '',
      //usersnap 632
      howHaveYouUsedYourEarnings: '',
      //usersnap 972
      isIncludeIntoCommonApp: false,
      isIncludeIntoUCApp: false,
      isIncludeIntoCSUApp: false,
    },
    onSubmit: (values) => {
      console.log('ActivitiesConfig values:', values);
    },
  });
  const renderFillAppStatus = () => {
    return (
      <span className="gm-item-card-header-icons">
        {formItem.values.isIncludeIntoCommonApp && (
          <img
            alt="cm"
            src="/assets/icons/icon-cm.svg"
            className="mr-1"
          />
        )}
        {formItem.values.isIncludeIntoUCApp && (
          <img
            alt="uc"
            src="/assets/icons/icon-uc.svg"
            className="mr-1"            
          />
        )}
      </span>
    );
  };
  const [expanded, setExpanded] = React.useState(props.expandAll || false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
    props.handleExpandActive(expanded);
  };

  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (check && props.indexExpand === props.indexNumber) {
      setExpanded(true);
      setTimeout(() => {
        if (elementRef.current) {
          elementRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    } else {
      setExpanded(props.expandAll || false);
    }
    if (props.item?.categoryUC) {
      formItem.setValues({
        ...formItem.values,
        ...props.item,
        activityType: mapActivityType(props.item.activityType) || formItem.values.activityType,
        programNameForEducationPreparation:
          mapEduPrepProgram(props.item.programNameForEducationPreparation) ||
          formItem.values.programNameForEducationPreparation,
        isInvolvedLeadershipRole: BoolToString(props.item.isInvolvedLeadershipRole),
        isCurrentlyParticipatingActivity: BoolToString(props.item.isCurrentlyParticipatingActivity),
        isIntendParticipateSimilarActivity: BoolToString(
          props.item.isIntendParticipateSimilarActivity
        ),
      });
    } else {
      // setIsShowMore(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.expandAll]);
  React.useEffect(() => {
    if (formItem.dirty) {
      props.handleSave({ ...formItem.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItem.values]);
  // const [isShowAllForm, setIsShowMore] = React.useState(false);
  const [labelWhatDoYouDo, setLabelWhatDoYouDo] = React.useState<any>({
    label: 'What did you do? *',
    feedback: <></>,
  });
  React.useEffect(() => {
    switch (formItem.values.categoryUC) {
      case 'Educational Preparation Program':
        setLabelWhatDoYouDo({
          label: 'Briefly Describe the Program',
          feedback: (
            <div>
              <div>
                Think about the program's main focus, your experience, and what you accomplished and
                learned while participating in the program.
              </div>
              <div>
                *The UC application does not ask for your position or title, consider including it
                in your description if appropriate.
              </div>
            </div>
          ),
        });
        break;
      case 'Other Coursework':
        setLabelWhatDoYouDo({
          label: 'Briefly Describe the course *',
          feedback: `What program or school offered the course? Also, think about describing the major themes or topics the course covered, as well as what knowledge or skills you learned.`,
        });
        break;
      case 'Work Experience':
        setLabelWhatDoYouDo({
          label: 'What were your job responsibilities?',
          feedback: ``,
        });
        break;
      default:
        setLabelWhatDoYouDo({
          label: 'What did you do? *',
          feedback: '',
        });
        break;
    }
  }, [formItem.values.categoryUC]);

  const buildHeader = () => {
    let category = formItem.values.categoryUC;

    const details = [];
    details.push(category);
    if (category === 'Work Experience') {
      details.push(formItem.values.workExperience_whereDidYouWork);
      details.push(formItem.values.workExperience_jobTitle);
    } else {
      if (category !== 'Volunteer/Community Service') {
        details.push(formItem.values.programName);
      }

      if (category === 'Volunteer/Community Service') {
        details.push(formItem.values.organizationVolunteeredFor);
      }

      details.push(formItem.values.positionDescription);
    }
    return details.length > 0 ? `${details.filter((v) => !!v).join(' - ')}` : category;
  };

  return (
    <div className="m-0" ref={elementRef}>
      <Card className="w-100 my-3 px-0 gm-item-card">
        <CardActions
          disableSpacing
          onClick={handleExpandClick}
          className="gm-item-card-header"
          sx={{
            backgroundColor: expanded ? '#AAEBF7' : 'transparent',
            borderBottom: expanded ? '2px solid #4D88F0' : '1px solid #E0E0E0',
          }}
        >
          <div className="w-100 d-flex justify-content-between">
            <span className="p-1 w-100">
              {buildHeader() || `Activity ${props.indexNumber}`}
              {renderFillAppStatus()}
            </span>
            <strong className="gm-item-card-header-index text-field-light">{props.indexNumber}</strong>
          </div>
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <div id="Activity_Details"></div>
            <Grid container spacing={isMobile ? 1 : 2} className="px-3 py-3 mb-3">
              <Grid xs={6}>
                <SelectInput
                  logo="span-uc-logo"
                  form={formItem}
                  option={[
                    'Educational Preparation Program',
                    'Extracurricular Activity',
                    'Other Coursework',
                    'Volunteer/Community Service',
                    'Work Experience',
                  ]}
                  label="Category *"
                  isSatTest={true}
                  values={formItem.values.categoryUC}
                  name="categoryUC"
                  onChange={formItem.handleChange}
                  isMissing={!formItem.values.categoryUC && check}
                  disabled={disabled}
                />
              </Grid>
              <Grid xs={6}>
                <SelectInput
                  logo="span-cm-logo"
                  form={formItem}
                  option={[
                    'Academic',
                    'Art',
                    'Athletics (other)',
                    'Athletics (JV or varsity)',
                    'Career readiness or practicums',
                    'Community service or volunteer work',
                    'Computer or technology',
                    'Cultural',
                    'Dance',
                    'Debate or speech',
                    'Employment or work (paid)',
                    'Environmental',
                    'Family Responsibilities',
                    'Foreign Language',
                    'Hobby',
                    'Internship',
                    'Journalism or publishing',
                    'LGBT+',
                    'Music',
                    'Religious',
                    'Research',
                    'Robotics',
                    'R.O.T.C or military',
                    'School Spirit',
                    'Science or math',
                    'Social justice or organizing',
                    'Student government or politics',
                    'Study abroad or foreign exchange',
                    'Theater or drama',
                    'Other',
                  ]}
                  label="Activity Type"
                  isSatTest={true}
                  values={formItem.values.activityType}
                  name="activityType"
                  onChange={formItem.handleChange}
                  disabled={disabled}
                />
              </Grid>
              <Grid xs={12} className="d-none mb-3">
                <SelectInput
                  className="d-none"
                  form={formItem}
                  option={optionFromArray([
                    'Academic Activity',
                    'Arts',
                    'Club/Organization',
                    'Family Responsibiltes',
                    'Sports',
                    'Volunteer Work',
                    'Work',
                    'Other',
                  ])}
                  label="Category *"
                  values={formItem.values.category}
                  name="category"
                  onChange={formItem.handleChange}
                  isMissing={!formItem.values.category && check}
                  disabled={disabled}
                />
              </Grid>

              {(formItem.values.activityType === 'Athletics (other)' ||
                formItem.values.activityType === 'Athletics (JV or varsity)' ||
                formItem.values.activityType === 'Athletics: Club' ||
                formItem.values.activityType === 'Athletics: JV/Varsity') && (
                <Grid xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <div className='mb-2'>Based on Activity type Athetics</div>
                  <SelectInput
                    logo="span-cm-logo"
                    mdCol="12"
                    form={formItem}
                    option={sportOptions}
                    label="Sport/team*"
                    name="sportOrTeam"
                    values={formItem.values.sportOrTeam}
                    onChange={formItem.handleChange}
                    disabled={disabled}
                  />
                </Grid>
              )}
              {formItem.values.categoryUC === 'Extracurricular Activity' && (
                <Grid xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <div className='mb-2'>Based on Extracurricular Activity</div>
                  <TextInput
                    logo="span-uc-logo"
                    label="What's the name of the activity?*"
                    isTestCsuLogo={true}
                    name="activityExperienceProgramName"
                    values={formItem.values.activityExperienceProgramName}
                    onChange={formItem.handleChange}
                    countText={60}
                    mdCol={12}
                    isOverCount={true}
                    isMissing={!formItem.values.activityExperienceProgramName && check}
                    disabled={disabled}
                  />
                </Grid>
              )}

              {formItem.values.categoryUC === 'Other Coursework' && (
                <Grid xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <div className='mb-2'>Based on Other Coursework</div>
                  <TextInput
                    logo="span-uc-logo"
                    isOverCount={true}
                    countText={60}
                    label="What was the course name?*"
                    values={formItem.values.otherCoursewordName}
                    onChange={formItem.handleChange}
                    name="otherCoursewordName"
                    disabled={disabled}
                  />
                </Grid>
              )}
              {formItem.values.categoryUC === 'Educational Preparation Program' && (
                <Grid xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <div className='mb-2'>Based on Educational Preparation Program</div>
                  <SelectInput
                    logo="span-uc-logo"
                    label="What was the program name? *"
                    isSatTest={true}
                    name="programNameForEducationPreparation"
                    onChange={formItem.handleChange}
                    values={formItem.values.programNameForEducationPreparation}
                    option={programForEduPreparOptions}
                    isMissing={
                      (!formItem.values.programNameForEducationPreparation ||
                        formItem.values.programNameForEducationPreparation === '- Select -') &&
                      check
                    }
                    disabled={disabled}
                  />
                  {formItem.values.programNameForEducationPreparation === 'Other' && (
                    <div className="py-4">
                    <TextInput
                      logo="span-uc-logo"
                      isOverCount={true}
                      countText={30}
                      label="Program Name *"
                      values={formItem.values.programNameForEducationPreparationSpecify}
                      onChange={formItem.handleChange}
                      name="programNameForEducationPreparationSpecify"
                      isMissing={
                        !formItem.values.programNameForEducationPreparationSpecify && check
                      }
                      disabled={disabled}
                    />
                    </div>
                  )}
                </Grid>
              )}
              {formItem.values.categoryUC === 'Volunteer/Community Service' && (
                <Grid xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <div className='mb-2'>Based on Volunteer/Community Service</div>
                  <TextInput
                    logo="span-cm-uc-logo"
                    label="What's the name of the organization, program, school or group you volunteered for?*"
                    mdCol="12"
                    isCuScuLogo={true}
                    isOverCount={true}
                    countText={60}
                    name="organizationVolunteeredFor"
                    values={formItem.values.organizationVolunteeredFor}
                    onChange={formItem.handleChange}
                    isMissing={!formItem.values.organizationVolunteeredFor && check}
                    disabled={disabled}
                  />
                  <TextAreaInput
                    className="uc-flag"
                    mdCol="12"
                    countText={250}
                    isOverCount={true}
                    label="Please describe the organization, program, school or group. *"
                    name="describeOrganization"
                    values={formItem.values.describeOrganization}
                    onChange={formItem.handleChange}
                    isMissing={!formItem.values.describeOrganization && check}
                    disabled={disabled}
                  />
                </Grid>
              )}
              {formItem.values.categoryUC === 'Work Experience' && (
                <Grid container spacing={isMobile ? 1 : 2} xs={12} className="bg-light rounded p-2 m-2 mt-3">
                  <Grid xs={12}><div className='mb-2'>Based on Work Experience</div></Grid>
                  <Grid xs={12}>
                    <TextInput
                      logo="span-cm-uc-logo"
                      mdCol="12"
                      isOverCount={true}
                      countText={60}
                      label="Where did you work*?"
                      name="workExperience_whereDidYouWork"
                      values={formItem.values.workExperience_whereDidYouWork}
                      onChange={formItem.handleChange}
                      feedback={<span>Please tell us the name of the place where you worked.</span>}
                      isMissing={!formItem.values.workExperience_whereDidYouWork && check}
                      isCuScuLogo={true}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <TextAreaInput
                      logo="span-uc-logo"
                      mdCol="12"
                      countText={250}
                      isOverCount={true}
                      label="Please briefly describe the company or organization where you worked. *"
                      name="describeCompany"
                      values={formItem.values.describeCompany}
                      onChange={formItem.handleChange}
                      isMissing={!formItem.values.describeCompany && check}
                      isCMLogo={true}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <TextInput
                      logo="span-cm-uc-logo"
                      mdCol="12"
                      isOverCount={true}
                      countText={60}
                      label="What was your job title/position*"
                      name="workExperience_jobTitle"
                      values={formItem.values.workExperience_jobTitle}
                      onChange={formItem.handleChange}
                      isMissing={!formItem.values.workExperience_jobTitle && check}
                      isCuScuLogo={true}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <div className="text-dark span-uc-logo">
                      <div
                        style={{
                          display: 'inline-block',
                          backgroundColor:
                            !formItem.values.workExperience_workAfterTwelfthGrade &&
                            !formItem.values.workExperience_workWhenEleventhGrade &&
                            !formItem.values.workExperience_workWhenNinthGrade &&
                            !formItem.values.workExperience_workWhenTenthGrade &&
                            !formItem.values.workExperience_workWhenTwelfthGrade &&
                            check
                              ? '#FFEBEB'
                              : '',
                        }}
                      >
                        When did you work at this job? *
                      </div>
                    </div>
                  </Grid>
                  <Grid xs={12}>
                    <div className="text-dark fs-7">
                      We'd like to know when you worked at this job and the number of hours per week
                      you worked. It's ok to estimate, but try to be as accurate as possible. If you
                      worked during the summer, select the grade year before that summer.
                    </div>
                  </Grid>

                  <Grid xs={12}>
                    <Card className="bg-body my-1 pb-2">
                      <Grid container spacing={isMobile ? 1 : 2} className="px-2">
                        <Grid xs={12}>
                          <CheckboxInput
                            name="workExperience_workWhenNinthGrade"
                            values={formItem.values.workExperience_workWhenNinthGrade}
                            onChange={formItem.handleChange}
                            label="9th Grade"
                            disabled={disabled}
                          />
                        </Grid>
                        {formItem.values.workExperience_workWhenNinthGrade && (
                          <>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_ninthGradeSchoolYear"
                                values={formItem.values.workExperience_ninthGradeSchoolYear}
                                onChange={formItem.handleChange}
                                label="School Year"
                                disabled={disabled}
                              />
                              <TextInput
                                hidden={!formItem.values.workExperience_ninthGradeSchoolYear}
                                mdCol={12}
                                type="number"
                                label="Hours Per week"
                                name="workExperience_ninthGradeSchoolYearHoursPerWeek"
                                onChange={formItem.handleChange}
                                values={
                                  formItem.values.workExperience_ninthGradeSchoolYearHoursPerWeek
                                }
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>

                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_ninthGradeSummer"
                                values={formItem.values.workExperience_ninthGradeSummer}
                                onChange={formItem.handleChange}
                                label="Summer"
                                disabled={disabled}
                              />
                              <TextInput
                                hidden={!formItem.values.workExperience_ninthGradeSummer}
                                mdCol={12}
                                label="Hours Per week"
                                type="number"
                                name="workExperience_ninthGradeSummerHoursPerWeek"
                                values={formItem.values.workExperience_ninthGradeSummerHoursPerWeek}
                                onChange={formItem.handleChange}
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid xs={12}>
                    <Card className="bg-body my-1 pb-2">
                      <Grid container spacing={isMobile ? 1 : 2} className="px-2">
                        <Grid xs={12}>
                          <CheckboxInput
                            name="workExperience_workWhenTenthGrade"
                            values={formItem.values.workExperience_workWhenTenthGrade}
                            onChange={formItem.handleChange}
                            label="10th Grade"
                            disabled={disabled}
                          />
                        </Grid>
                        {formItem.values.workExperience_workWhenTenthGrade && (
                          <>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_tenthGradeSchoolYear"
                                values={formItem.values.workExperience_tenthGradeSchoolYear}
                                onChange={formItem.handleChange}
                                label="School Year"
                                disabled={disabled}
                              />
                              <TextInput
                                hidden={!formItem.values.workExperience_tenthGradeSchoolYear}
                                mdCol={12}
                                type="number"
                                label="Hours Per week"
                                name="workExperience_tenthGradeSchoolYearHoursPerWeek"
                                onChange={formItem.handleChange}
                                values={
                                  formItem.values.workExperience_tenthGradeSchoolYearHoursPerWeek
                                }
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_tenthGradeSummer"
                                values={formItem.values.workExperience_tenthGradeSummer}
                                onChange={formItem.handleChange}
                                label="Summer"
                                disabled={disabled}
                              />
                              <TextInput
                                hidden={!formItem.values.workExperience_tenthGradeSummer}
                                mdCol={12}
                                label="Hours Per week"
                                type="number"
                                name="workExperience_tenthGradeSummerHoursPerWeek"
                                values={formItem.values.workExperience_tenthGradeSummerHoursPerWeek}
                                onChange={formItem.handleChange}
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid xs={12}>
                    <Card className="bg-body my-1 pb-2">
                      <Grid container spacing={isMobile ? 1 : 2} className="px-2">
                        <Grid xs={12}>
                          <CheckboxInput
                            name="workExperience_workWhenEleventhGrade"
                            values={formItem.values.workExperience_workWhenEleventhGrade}
                            onChange={formItem.handleChange}
                            label="11th Grade"
                            disabled={disabled}
                          />
                        </Grid>
                        {formItem.values.workExperience_workWhenEleventhGrade && (
                          <>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_eleventhGradeSchoolYear"
                                values={formItem.values.workExperience_eleventhGradeSchoolYear}
                                onChange={formItem.handleChange}
                                label="School Year"
                                disabled={disabled}
                                />
                              <TextInput
                                hidden={!formItem.values.workExperience_eleventhGradeSchoolYear}
                                mdCol={12}
                                type="number"
                                label="Hours Per week"
                                name="workExperience_eleventhGradeSchoolYearHoursPerWeek"
                                onChange={formItem.handleChange}
                                values={
                                  formItem.values.workExperience_eleventhGradeSchoolYearHoursPerWeek
                                }
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                                />
                            </Grid>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_eleventhGradeSummer"
                                values={formItem.values.workExperience_eleventhGradeSummer}
                                onChange={formItem.handleChange}
                                label="Summer"
                                disabled={disabled}
                                />
                              <TextInput
                                mdCol="12"
                                hidden={!formItem.values.workExperience_eleventhGradeSummer}
                                label="Hours Per week"
                                type="number"
                                name="workExperience_eleventhGradeSummerHoursPerWeek"
                                values={
                                  formItem.values.workExperience_eleventhGradeSummerHoursPerWeek
                                }
                                onChange={formItem.handleChange}
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid xs={12}>
                    <Card className="bg-body my-1 pb-2">
                      <Grid container spacing={isMobile ? 1 : 2} className="px-2">
                        <Grid xs={12}>
                          <CheckboxInput
                            name="workExperience_workWhenTwelfthGrade"
                            values={formItem.values.workExperience_workWhenTwelfthGrade}
                            onChange={formItem.handleChange}
                            label="12th Grade"
                            disabled={disabled}
                          />
                        </Grid>
                        {formItem.values.workExperience_workWhenTwelfthGrade && (
                          <>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_twelfthGradeSchoolYear"
                                values={formItem.values.workExperience_twelfthGradeSchoolYear}
                                onChange={formItem.handleChange}
                                label="School Year"
                                disabled={disabled}
                                />
                              <TextInput
                                mdCol="12"
                                hidden={!formItem.values.workExperience_twelfthGradeSchoolYear}
                                type="number"
                                label="Hours Per week"
                                name="workExperience_twelfthGradeSchoolYearHoursPerWeek"
                                onChange={formItem.handleChange}
                                values={
                                  formItem.values.workExperience_twelfthGradeSchoolYearHoursPerWeek
                                }
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_twelfthGradeSummer"
                                values={formItem.values.workExperience_twelfthGradeSummer}
                                onChange={formItem.handleChange}
                                label="Summer"
                                disabled={disabled}
                                />
                              <TextInput
                                mdCol="12"
                                hidden={!formItem.values.workExperience_twelfthGradeSummer}
                                label="Hours Per week"
                                type="number"
                                name="workExperience_twelfthGradeSummerHoursPerWeek"
                                values={
                                  formItem.values.workExperience_twelfthGradeSummerHoursPerWeek
                                }
                                onChange={formItem.handleChange}
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid xs={12}>
                    <Card className="bg-body my-1 pb-2">
                      <Grid container spacing={isMobile ? 1 : 2} className="px-2">
                        <Grid xs={12}>
                          <CheckboxInput
                            name="workExperience_workAfterTwelfthGrade"
                            values={formItem.values.workExperience_workAfterTwelfthGrade}
                            onChange={formItem.handleChange}
                            label="After 12th"
                            disabled={disabled}
                          />
                        </Grid>
                        {formItem.values.workExperience_workAfterTwelfthGrade && (
                          <>
                            <Grid xs={6}>
                              <SelectInput
                                option={activityNumberOfYearAfter12}
                                name="workExperience_afterTwelfthGradeSummerYear"
                                values={formItem.values.workExperience_afterTwelfthGradeSummerYear}
                                onChange={formItem.handleChange}
                                label="Number of Years *"
                                isMissing={
                                  !formItem.values.workExperience_afterTwelfthGradeSummerYear &&
                                  check
                                }
                                disabled={disabled}
                              />
                            </Grid>
                            <Grid xs={6}>
                              <SelectInput
                                option={activityNumberOfMonthAfter12}
                                name="workExperience_afterTwelfthGradeSummerMonth"
                                values={
                                  formItem.values.workExperience_afterTwelfthGradeSummerMonth
                                }
                                onChange={formItem.handleChange}
                                label="Number of Months *"
                                isMissing={
                                  !formItem.values.workExperience_afterTwelfthGradeSummerMonth &&
                                  check
                                }
                                disabled={disabled}
                              />
                            </Grid>
                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_afterTwelfthGradeSchoolYear"
                                values={formItem.values.workExperience_afterTwelfthGradeSchoolYear}
                                onChange={formItem.handleChange}
                                label="School Year"
                                disabled={disabled}
                              />
                              <TextInput
                                mdCol="12"
                                hidden={
                                  !formItem.values.workExperience_afterTwelfthGradeSchoolYear
                                }
                                type="number"
                                label="Hours Per week"
                                name="workExperience_afterTwelfthGradeSchoolYearHoursPerWeek"
                                onChange={formItem.handleChange}
                                values={
                                  formItem.values
                                    .workExperience_afterTwelfthGradeSchoolYearHoursPerWeek
                                }
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>

                            <Grid xs={6}>
                              <CheckboxInput
                                name="workExperience_afterTwelfthGradeSummer"
                                values={formItem.values.workExperience_afterTwelfthGradeSummer}
                                onChange={formItem.handleChange}
                                label="Summer"
                                disabled={disabled}
                              />
                              <TextInput
                                hidden={!formItem.values.workExperience_afterTwelfthGradeSummer}
                                mdCol="12"
                                label="Hours Per week"
                                type="number"
                                name="workExperience_afterTwelfthGradeSummerHoursPerWeek"
                                values={
                                  formItem.values
                                    .workExperience_afterTwelfthGradeSummerHoursPerWeek
                                }
                                onChange={formItem.handleChange}
                                min={0}
                                max={999}
                                decimalStep={0.1}
                                disabled={disabled}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid xs={12}>
                    <YesNoSwitch
                      values={formItem.values.isStillWork}
                      onChange={formItem.handleChange}
                      name="isStillWork"
                      className="my-3"
                      label="Do you still work at this job? *"
                      isMissing={!formItem.values.isStillWork && check}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid xs={6}>
                    <DatePickerInput
                      label="Start date"
                      name="startDate"
                      values={formItem.values.startDate}
                      onChange={(value) => formItem.setFieldValue('startDate', value)}
                      disabled={disabled}
                    />
                  </Grid>
                  {formItem.values.isStillWork === 'false' && (
                    <Grid xs={6}>
                      <DatePickerInput
                        label="End date"
                        logo="span-uc-logo"
                        name="endDate"
                        values={formItem.values.endDate}
                        onChange={(value) => formItem.setFieldValue('endDate', value)}
                        disabled={disabled}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
              <Grid xs={12}>
                <YesNoSwitch
                  className="common-flag my-2"
                  label="Did you have a specific position or leadership role?*"
                  name="isInvolvedLeadershipRole"
                  values={formItem.values.isInvolvedLeadershipRole}
                  onChange={formItem.handleChange}
                  isMissing={!formItem.values.isInvolvedLeadershipRole && check}
                  disabled={disabled}
                />
              </Grid>
              {formItem.values.isInvolvedLeadershipRole === 'true' && (
                <Grid xs={12}>
                  <TextInput
                    logo="span-cm-logo"
                    label="What was your role?*"
                    name="positionDescription"
                    isTestCsuLogo={true}
                    values={formItem.values.positionDescription}
                    onChange={formItem.handleChange}
                    countText={50}
                    isOverCount={true}
                    mdCol={12}
                    isMissing={!formItem.values.positionDescription && check}
                    disabled={disabled}
                  />
                </Grid>
              )}
              <Grid xs={12}>
                <TextInput
                  hidden={
                    formItem.values.categoryUC === 'Volunteer/Community Service' ||
                    formItem.values.categoryUC === 'Work Experience'
                  }
                  logo="span-cm-logo"
                  label="Organization Name*"
                  name="programName"
                  isTestCsuLogo={true}
                  values={formItem.values.programName}
                  onChange={formItem.handleChange}
                  countText={100}
                  isOverCount={true}
                  mdCol={12}
                  disabled={disabled}
                />
              </Grid>
              <Grid xs={12}>
                <TextAreaInput
                  // hidden={formItem.values.categoryUC === 'Work Experience'}
                  // Yêu cầu của CM App
                  logo="span-cm-logo"
                  mdCol="12"
                  countText={150}
                  isOverCount={true}
                  label="Please describe this activity, including what you accomplished and any recognition you received, etc."
                  name="describeActivity"
                  isCMLogo={true}
                  values={formItem.values.describeActivity}
                  onChange={formItem.handleChange}
                  disabled={disabled}
                />
              </Grid>

              <Grid xs={12}>
                <TextAreaInput
                  logo="span-uc-logo"
                  isCMLogo={true}
                  mdCol="12"
                  countText={350}
                  isOverCount={true}
                  label={labelWhatDoYouDo.label}
                  name="whatDidYouDo"
                  values={formItem.values.whatDidYouDo}
                  onChange={formItem.handleChange}
                  feedback={labelWhatDoYouDo.feedback}
                  isMissing={!formItem.values.whatDidYouDo && check}
                  disabled={disabled}
                />
              </Grid>
              <>
                <Grid xs={12}>
                  <GroupCheckbox
                    label="Participation grade levels*"
                    form={formItem}
                    mdCol={2}
                    options={[
                      {
                        value: 'isGrade9ParticipationLevels',
                        label: '9',
                      },
                      {
                        value: 'isGrade10ParticipationLevels',
                        label: '10',
                      },
                      {
                        value: 'isGrade11ParticipationLevels',
                        label: '11',
                      },
                      {
                        value: 'isGrade12ParticipationLevels',
                        label: '12',
                      },
                      {
                        value: 'isPostGraduateParticipationLevels',
                        label: 'Post-graduate (can select mulitple)',
                        mdCol: 12,
                      },
                    ]}
                    tooltips="If you participated in the program during the summer, select the grade you were in before that summer."
                    isMissing={
                      !formItem.values.isGrade9ParticipationLevels &&
                      !formItem.values.isGrade10ParticipationLevels &&
                      !formItem.values.isGrade11ParticipationLevels &&
                      !formItem.values.isGrade12ParticipationLevels &&
                      !formItem.values.isPostGraduateParticipationLevels &&
                      check
                    }
                    disabled={disabled}
                  />
                </Grid>
                <Grid xs={12}>
                  <YesNoSwitch
                    className="coallition-flag mx-1 py-3"
                    label="Are you currently participating in this activity or experience?"
                    name="isCurrentlyParticipatingActivity"
                    values={formItem.values.isCurrentlyParticipatingActivity}
                    onChange={(e: any) => {
                      formItem.handleChange(e);
                    }}
                    disabled={disabled}
                  />
                </Grid>
                <Grid xs={12}>
                  <GroupCheckbox
                    className="py-3"
                    label="Timing of Participation*"
                    columns={1}
                    form={formItem}
                    options={[
                      {
                        value: 'timmingOfParticipation_duringYear',
                        label: 'During school year',
                        mdCol: 4,
                      },
                      {
                        value: 'timmingOfParticipation_duringBreak',
                        label: 'During school break',
                        mdCol: 4,
                      },
                      { value: 'timmingOfParticipation_allYear', label: 'All year', mdCol: 4 },
                    ]}
                    isMissing={
                      !formItem.values.timmingOfParticipation_duringYear &&
                      !formItem.values.timmingOfParticipation_duringBreak &&
                      !formItem.values.timmingOfParticipation_allYear &&
                      check
                    }
                    disabled={disabled}
                  />
                </Grid>
                <Grid xs={6}>
                  <TextInput
                    max={168}
                    type="number"
                    label="Hours per Week*"
                    name="hoursPerWeek"
                    values={formItem.values.hoursPerWeek}
                    onChange={formItem.handleChange}
                    isMissing={!formItem.values.hoursPerWeek && check}
                    disabled={disabled}
                  />
                </Grid>
                <Grid xs={6}>
                  <TextInput
                    max={52}
                    type="number"
                    label="Weeks per Year*"
                    name="weeksPerYear"
                    values={formItem.values.weeksPerYear}
                    onChange={formItem.handleChange}
                    isMissing={!formItem.values.weeksPerYear && check}
                    disabled={disabled}
                  />
                </Grid>
                <Grid xs={12}>
                  <YesNoSwitch
                    label="I intend to participate in a similar activity in college.*"
                    className="mx-1"
                    logo="span-cm-logo"
                    name="isIntendParticipateSimilarActivity"
                    values={formItem.values.isIntendParticipateSimilarActivity}
                    onChange={(e: any) => {
                      formItem.handleChange(e);
                    }}
                    isMissing={!formItem.values.isIntendParticipateSimilarActivity && check}
                    disabled={disabled}
                  />
                </Grid>
              </>
              <Grid id="Selected_Apps" xs={12} className="mt-3">
                <GroupCheckbox
                  label="Select the applications you would like to include this activity on. *See App Notes above for activity limits"
                  form={formItem}
                  mdCol={3}
                  columns={4}
                  options={[
                    {
                      value: 'isIncludeIntoCommonApp',
                      label: 'Common App',
                    },
                    {
                      value: 'isIncludeIntoUCApp',
                      label: 'UC App',
                    },
                  ]}
                  disabled={disabled}
                />
              </Grid>
              <Grid xs={12} container justifyContent="flex-end">
                <Grid xs={6} md={3} className="p-3">
                  <Button
                    className="w-100"
                    onClick={() => {
                      props.handleSave({
                        ...formItem.values,
                        isInvolvedLeadershipRole: formatBoolean(
                          formItem.values.isInvolvedLeadershipRole
                        ),
                        isCurrentlyParticipatingActivity: formatBoolean(
                          formItem.values.isCurrentlyParticipatingActivity
                        ),
                        isIntendParticipateSimilarActivity: formatBoolean(
                          formItem.values.isIntendParticipateSimilarActivity
                        ),
                      });
                      setExpanded(false);
                    }}
                    disabled={disabled}
                  >
                    Save Info
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    </div>
  );
};

export default ActivitiesConfig;
